import { useState, useEffect, useRef } from 'react';
import { toast } from '../components/ui/use-toast';

interface Company {
  id: string;
  name: string;
  domain?: string;
  business_name?: string;
  business_description?: string;
  business_key_features?: string[];
  logistics?: boolean;
  packaging?: boolean;
  d2c?: boolean;
  b2b?: boolean;
  b2c?: boolean;
  type: 'good' | 'bad';
  isLoading?: boolean;
}

export const useCompanies = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const pollingIntervals = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const pollingStartTimes = useRef<Map<string, number>>(new Map());

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      pollingIntervals.current.forEach(interval => clearInterval(interval));
      pollingIntervals.current.clear();
      pollingStartTimes.current.clear();
    };
  }, []);

  const extractDomainFromName = (companyName: string): string => {
    if (companyName.includes('.')) {
      return companyName.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '');
    }
    return `${companyName.toLowerCase().replace(/\s+/g, '')}.com`;
  };

  const stopPolling = (companyId: string) => {
    const interval = pollingIntervals.current.get(companyId);
    if (interval) {
      clearInterval(interval);
      pollingIntervals.current.delete(companyId);
      pollingStartTimes.current.delete(companyId);
    }
  };

  const startPolling = (companyId: string, companyName: string, domain: string) => {
    // Stop any existing polling for this company
    stopPolling(companyId);
    
    const startTime = Date.now();
    pollingStartTimes.current.set(companyId, startTime);
    
    const pollInterval = setInterval(async () => {
      const currentTime = Date.now();
      const elapsedTime = currentTime - startTime;
      
      // Stop polling after 2 minutes (120,000 ms)
      if (elapsedTime > 120000) {
        stopPolling(companyId);
        setCompanies(prev => 
          prev.map(company => 
            company.id === companyId 
              ? { 
                  ...company, 
                  domain,
                  business_name: companyName,
                  business_description: 'Processing timeout - data may be incomplete',
                  business_key_features: [],
                  isLoading: false 
                }
              : company
          )
        );
        
        toast({
          title: "Processing timeout",
          description: `${companyName} took too long to process`,
          variant: "destructive"
        });
        return;
      }
      
      try {
        const response = await fetch(`https://cache.featrix.com/scrape-raw/${domain}`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch data for ${companyName}`);
        }
        
        const data = await response.json();
        
        // Check if still processing
        if (data[0]?.status === 'new' || data[0]?.status === 'rescrape') {
          console.log(`Still processing ${domain}, will check again in 10 seconds...`);
          return; // Continue polling
        }
        
        // Data is ready, stop polling and process it
        stopPolling(companyId);
        
        const summary = data[0]?.['llm-json-summary'];
        
        if (summary) {
          const companyData = {
            domain,
            business_name: summary?.business_name || companyName,
            business_description: summary?.business_description || '',
            business_key_features: summary?.business_key_features?.slice(0, 3) || [],
            logistics: summary?.business_is_logistics_related || false,
            packaging: summary?.['sub-packaging'] ? true : false,
            d2c: summary?.business_is_d2c || false,
            b2b: summary?.business_is_b2b || false,
            b2c: summary?.business_is_b2c || false,
            isLoading: false
          };

          setCompanies(prev => 
            prev.map(company => 
              company.id === companyId 
                ? { ...company, ...companyData }
                : company
            )
          );
        } else {
          // No summary available but not processing anymore
          setCompanies(prev => 
            prev.map(company => 
              company.id === companyId 
                ? { 
                    ...company, 
                    domain,
                    business_name: companyName,
                    business_description: 'Data processed but no summary available',
                    business_key_features: [],
                    isLoading: false 
                  }
                : company
            )
          );
        }
      } catch (error) {
        console.error('Error during polling:', error);
        stopPolling(companyId);
        
        setCompanies(prev => 
          prev.map(company => 
            company.id === companyId 
              ? { 
                  ...company, 
                  domain,
                  business_name: companyName,
                  business_description: 'Failed to fetch company data',
                  business_key_features: [],
                  isLoading: false 
                }
              : company
          )
        );
        
        toast({
          title: "Error",
          description: `Failed to fetch data for ${companyName}`,
          variant: "destructive"
        });
      }
    }, 10000); // Poll every 10 seconds
    
    pollingIntervals.current.set(companyId, pollInterval);
  };

  const fetchCompanyData = async (companyId: string, companyName: string) => {
    try {
      const domain = extractDomainFromName(companyName);
      const response = await fetch(`https://cache.featrix.com/scrape-raw/${domain}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch data for ${companyName}`);
      }
      
      const data = await response.json();
      
      // Check if the domain is new and needs polling
      if (data[0]?.status === 'new' || data[0]?.status === 'rescrape') {
        console.log(`Domain ${domain} is new, starting polling...`);
        startPolling(companyId, companyName, domain);
        return;
      }
      
      const summary = data[0]?.['llm-json-summary'];
      
      const companyData = {
        domain,
        business_name: summary?.business_name || companyName,
        business_description: summary?.business_description || '',
        business_key_features: summary?.business_key_features?.slice(0, 3) || [],
        logistics: summary?.business_is_logistics_related || false,
        packaging: summary?.['sub-packaging'] ? true : false,
        d2c: summary?.business_is_d2c || false,
        b2b: summary?.business_is_b2b || false,
        b2c: summary?.business_is_b2c || false,
        isLoading: false
      };

      setCompanies(prev => 
        prev.map(company => 
          company.id === companyId 
            ? { ...company, ...companyData }
            : company
        )
      );
    } catch (error) {
      console.error('Error fetching company data:', error);
      
      setCompanies(prev => 
        prev.map(company => 
          company.id === companyId 
            ? { 
                ...company, 
                domain: extractDomainFromName(companyName),
                business_name: companyName,
                business_description: 'Failed to fetch company data',
                business_key_features: [],
                isLoading: false 
              }
            : company
        )
      );
      
      toast({
        title: "Error",
        description: `Failed to fetch data for ${companyName}`,
        variant: "destructive"
      });
    }
  };

  const addCompanies = (companyNames: string[], type: 'good' | 'bad') => {
    if (companies.length + companyNames.length > 100) {
      toast({
        title: "Too many companies",
        description: "You can add up to 100 companies total.",
        variant: "destructive"
      });
      return false;
    }

    const newCompanies = companyNames.map((name, index) => ({
      id: `${type}-${Date.now()}-${index}`,
      name,
      type,
      isLoading: true
    }));

    setCompanies(prev => [...prev, ...newCompanies]);

    newCompanies.forEach(company => {
      fetchCompanyData(company.id, company.name);
    });

    return true;
  };

  const removeCompany = (id: string) => {
    stopPolling(id);
    setCompanies(prev => prev.filter(company => company.id !== id));
  };

  const parseCompanies = (input: string): string[] => {
    return input
      .split(/[,\n]/)
      .map(company => company.trim())
      .filter(company => company.length > 0);
  };

  return {
    companies,
    addCompanies,
    removeCompany,
    parseCompanies
  };
}; 